/**
 * 音階
 *
 * 【なぜ音階を先に決めるか】
 * 手続きで曲を作るとき、音を自由に選ばせると必ず濁る。
 * **選べる音をあらかじめ狭めておけば、どう選んでも濁らない。**
 * 出鱈目に鳴らして「らしく」聞こえるのは、音階がその仕事をしているからである。
 *
 * 【なぜこの音階か】
 * 都節（みやこぶし）は近世邦楽の音階で、箏や三味線の調弦がこれに近い。
 * 半音（第2音）が入るのが特徴で、これが「日本の音」と聞こえる正体である。
 * 陽旋法（田舎節）は半音を持たず、明るく開ける。
 *
 * **史実の音楽を再現しているとは言わない。** 戦国期に何がどう鳴っていたかは
 * 確かめられないので、家紋を描かなかったのと同じ線を引く（`layout.ts`）。
 * ここにあるのは「近世邦楽の音階」であって「戦国の音楽」ではない。
 *
 * 【ここは純粋な関数の集まり】
 * `AudioContext` を知らない。数だけを扱うので vitest から直接検査できる。
 */

/** 音階の名前と、根音からの半音の隔たり */
export const SCALES = {
  /** 都節音階（陰旋法）。半音を含む。合戦・史実の緊張に使う */
  miyakobushi: [0, 1, 5, 7, 8],
  /** 律音階。半音が無く落ち着く。盤上の進行に使う */
  ritsu: [0, 2, 5, 7, 9],
  /** 民謡音階。もっとも素朴。決算・図鑑に使う */
  minyo: [0, 3, 5, 7, 10],
} as const satisfies Record<string, readonly number[]>;

export type ScaleName = keyof typeof SCALES;

/** MIDI 番号を周波数（Hz）に。69 = A4 = 440Hz という約束に従う */
export const midiToFreq = (midi: number): number => 440 * 2 ** ((midi - 69) / 12);

/**
 * 音階の第 n 音の MIDI 番号。
 *
 * `degree` は音階の外へも出られる（5音音階なら 5 で1オクターブ上の根音）。
 * **負の値も通す** — 低いほうへ伸ばせないと、伴奏が根音より下へ行けない。
 */
export function degreeToMidi(scale: ScaleName, root: number, degree: number): number {
  const steps = SCALES[scale];
  const n = steps.length;
  // JS の % は負で負を返すので、床除算で回す
  const octave = Math.floor(degree / n);
  const index = degree - octave * n;
  return root + octave * 12 + steps[index]!;
}

/** 音階の第 n 音の周波数。曲を書くときはこれだけ使う */
export const degreeToFreq = (scale: ScaleName, root: number, degree: number): number =>
  midiToFreq(degreeToMidi(scale, root, degree));

/**
 * 曲の速さ（1拍の秒数）。
 *
 * 場面ごとに変える。**速さは音色より強く気分を決める** —
 * 同じ音でも、遅ければ荘重に、速ければ急かして聞こえる。
 */
export const beatSeconds = (bpm: number): number => 60 / bpm;
